import algoliasearch from 'algoliasearch/lite'
import { graphql, HeadProps, PageProps } from 'gatsby'
import React, { useMemo, useState } from 'react'
import { AppHead, Layout } from '../components/common'
import { FirmTeaser, FirmTeaserProps } from '../components/firms'

import {
  FirmAlgoliaSearchResult,
  FirmTeaserMapper,
  Search,
  SearchBox,
  SearchRefinementClear,
  SearchResultInfiniteList,
  SearchStatus,
  SearchStatusPropsValue,
  withSearchResultMapper,
} from '../components/search'
import Refinement from '../components/search/refinement'
import { TitleHighlight } from '../components/ui/typo/TitleHighlight'
import { HomeHeading } from '../components/page/home/Heading'
import { SearchResultNb } from '../components/page/offers/list/SearchStatus'

const Cabinets: React.FC<PageProps<Queries.FirmPageQuery>> = ({ data, location }) => {
  const googleMapsApiKey = useMemo(() => data.site?.siteMetadata?.googleMaps?.apiKey || '', [data.site?.siteMetadata?.googleMaps?.apiKey])

  const refinementAttributes = [
    {
      attribute: 'jobTypes',
      name: 'Métier',
      type: 'list' as const,
      searchable: true,
    },
    {
      attribute: 'totalEmployees',
      name: 'Taille',
      type: 'list' as const,
    },
    {
      attribute: 'tools',
      name: 'Outils',
      type: 'list' as const,
      searchable: true,
    },
    {
      attribute: 'badges',
      name: 'Badges',
      type: 'list' as const,
      searchable: true,
    },
  ]

  const [searchStatus, setSearchStatus] = useState<SearchStatusPropsValue>()
  const appId = data.site?.siteMetadata?.algolia?.appId || ''
  const searchKey = data.site?.siteMetadata?.algolia?.searchkey || ''
  const indexName = data.site?.siteMetadata?.algolia?.firmsIndex || ''

  const searchClient = useMemo(() => algoliasearch(appId, searchKey), [])

  return (
    <Layout
      careerSite={false}
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div className="h-full bg-very-light-blue">
        <div className="container mx-auto">
          <Search algolia={{ searchClient, indexName }}>
            <SearchStatus onChange={(val) => setSearchStatus(val)} />
            <div className="pt-8 space-y-4 md:py-12 lg:py-14 xl:py-18">
              <div className="space-y-12">
                <div className="px-4 py-8 space-y-12 md:px-0">
                  <HomeHeading tag="h1">
                    Le cabinet qui te{' '}
                    <TitleHighlight>correspond</TitleHighlight> est ici
                  </HomeHeading>

                  <SearchBox
                    autoCompleteAttributeName="name"
                    searchClient={searchClient}
                    googleMapsApiKey={googleMapsApiKey}
                    placeholder={{
                      input: 'Cabinet, mots clés...',
                      place: 'Adresse ou ville...',
                    }}
                  />
                </div>
                <div className="flex items-center justify-center">
                  <Refinement attributes={refinementAttributes} />
                </div>
                <div className="items-center self-center justify-center hidden md:flex ">
                  <SearchRefinementClear attributes={refinementAttributes} />
                </div>
              </div>

              <div className="px-4 py-4 space-y-4 md:px-0">
                <SearchResultNb searchStatus={searchStatus} type="firms" />
                <SearchResultInfiniteList
                  resultComponent={withSearchResultMapper<
                    FirmAlgoliaSearchResult,
                    FirmTeaserProps
                  >(FirmTeaserMapper, FirmTeaser)}
                  type="firm"
                />
              </div>
            </div>
          </Search>
        </div>
      </div>
    </Layout>
  )
}

export default Cabinets

export const Head: React.FC<HeadProps<Queries.FirmPageQuery>> = ({ data }) => {
  const totalcount = data.allStrapiAccountingFirm?.totalCount ?? '370'
  const linkCanonical = data.site?.siteMetadata?.siteUrl
    ? `${data.site?.siteMetadata?.siteUrl}/cabinets`
    : null
  return (
    <AppHead
      title="Cabinets d'Expertise Comptable - Lamacompta"
      algoliaAppId={data.site?.siteMetadata?.algolia?.appId}
      metaDescription={`Retrouvez les cabinets d'expertise comptable attractifs qui recrutent ! ${totalcount} sont sur Lamacompta, découvrez leurs offres.`}
      metaTitle="Les Cabinets d'Expertise Comptable qui recrutent"
      linkCanonical={linkCanonical}
      metaImage="https://lamacompta.co/lamacompta-sharing-default.jpg"
    />
  )
}

export const query = graphql`
  query FirmPage {
    site {
      siteMetadata {
        siteUrl
        frontStrapiToken
        siteStrapiUrl
        algolia {
          appId
          searchkey
          firmsIndex
        }
        googleMaps {
          apiKey
        }
      }
    }
    allStrapiAccountingFirm {
      totalCount
    }
  }
`
